




















































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import Input from '@/components/Input.vue';
import ExclamationIcon from '@/components/icons/ExclamationIcon.vue';
import { BottomWrapper } from '../BottomWrapper.vue';

import * as PleasedApi from '@/common/api/api.pleased';
import * as PleasedModel from '@/common/api/models.pleased';
import waiting from '@/components/loading/waiting';
import { ConnectRequestEvent } from '@/common/types';
import { reportingService } from '@/common/reporting';
import { mixins, noop } from 'vue-class-component/lib/util';
import { MAX_DESCRIPTION_CHAR_NUM } from '@/common/config';
import * as HardcodedTags from '@/common/tags';
import { OverloadedChannel, SelectedChannel, SelectedChannelType } from '@/common/bot/enum/DeflectionLog';
import { ConnectToAgent } from '@/common/bot/types';
import { GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import ThresholdLimitModal from './ThresholdLimitModal.vue';
import LoadingIcon from '@/components/icons/LoadingIcon.vue';
import { notEmpty, waitFor } from '@/common/utils';
import { Conversation } from '../active-conversations/type';
import LogPageEventMixin, { PAGE_TRACKING } from '@/mixins/logPageEvent';
import { analytics, CMP_CONTEXTS, COMPONENT_EVENTS } from '@/common/logs';

const userModule = namespace('user');
const channelLoadingModule = namespace('channelLoading');


@Component({
  components: {
    Input,
    BottomWrapper,
    ExclamationIcon,
    ThresholdLimitModal,
    LoadingIcon,
  },
  directives: {
    waiting,
  },
})
export default class ContactOptions extends mixins(LogPageEventMixin) {
  @Prop({ default: () => ({ subject: '', description: '', email: '' }) }) readonly requestInfo!: { subject: string, description: string, email: string };
  @Prop({ default: () => ({
    showCall: true,
    showLiveChat: true,
  }) }) readonly showLoggedInOptions!: { showCall: boolean, showLiveChat: boolean, showOpenRequest: boolean };

  @Prop(Boolean) readonly showAddComment!: boolean;
  @Prop(Boolean) readonly showBack!: boolean;
  @Prop() readonly connectConfig!: ConnectToAgent | null;
  @Prop({ default: () => [] }) readonly ticketFields!: PleasedModel.TicketField[];
  @Prop() readonly userSelections!: PleasedModel.UserSelection[];
  @Prop() readonly showThresholdLimitModal!: boolean;
  @Prop() readonly activeConversationTicket!: Conversation;

  @userModule.State('isLogin') isLogin!: boolean;

  @channelLoadingModule.State('isLiveChatLoading') readonly isLiveChatLoading!: boolean;
  @channelLoadingModule.State('isVoiceChatLoading') readonly isVoiceChatLoading!: boolean;

  readonly MAX_DESCRIPTION_CHAR_NUM = MAX_DESCRIPTION_CHAR_NUM;
  readonly DISABLED_OPTION_WAIT_TIME = 60;

  get [PAGE_TRACKING] () {
    return {
      name: `${this.trackLogContext}${COMPONENT_EVENTS.CONNECT_OPTIONS}`,
      ...(this.isFromActiveConversation ? { selection: this.activeConversationTicket.id } : {}),
    }
  }

  get openRequestField () {
    return {
      id: -10000,
      displayName: this.$t('global.description') as string,
      placeholder: this.connectConfig?.descriptionMessage || this.$t('global.describe') as string,
      name: 'description',
      required: true,
    };
  }

  get descriptionLength () {
    return this.requestInfo.description?.length || 0;
  }

  get isDescriptionExceed () {
    return this.descriptionLength > MAX_DESCRIPTION_CHAR_NUM;
  }

  savedRequestInfo: { subject: string, description: string, email: string } | null = null;

  openRequestError = {
    description: false,
  }

  loading = false;
  isOpeningRequest = false;
  queuePosition = 0;

  errInfo = {
    isErr: false,
    message: '',
  };

  voiceChatAgentsOnline = false;
  waitTimes = {
    chat: null as null|string,
    call: null as null|string,
    order: {
      chat: 1,
      call: 2,
    },
  }

  onlineAgents = {
    chat: null as null|boolean,
    call: null as null|boolean,
  }

  longTimeWaiting = {
    MIN_WAIT_TIME: 10,
    selectedOption: null as 'liveChat' |'call' | null,
  };

  refreshChannelsLoading = false;
  refreshChannelsThrottle = false;

  forceHide = {
    chat: false,
    call: false
  }

  get buttons () {
    if (!this.isOpeningRequest) {
      const shouldRefreshChat = this.showLoggedInOptions.showLiveChat && !this.hasLiveChatOnlineAgents;
      const shouldRefreshCalls = this.showLoggedInOptions.showCall && !this.hasCallOnlineAgents;
      if (shouldRefreshChat || shouldRefreshCalls) {
        return [{
          name: this.t('page.connect.refresh_channels.text'),
          id: 'refresh',
          isActive: false,
          appendIcon: this.refreshChannelsLoading ? LoadingIcon : null,
        }];
      }

      return [];
    }

    return [{
      name: this.t('page.connect.open_request.text'),
      id: 'open-request',
      isActive: true,
    }];
  }

  get isLiveChatLongTimeWaiting () {
    if (!this.showLiveChat || !this.hasLiveChatOnlineAgents) return false;

    const timeWaiting = Number(this.waitTimes.chat?.slice(1));
    return timeWaiting >= this.longTimeWaiting.MIN_WAIT_TIME;
  }

  get isCallLongTimeWaiting () {
    if (!this.showCall || !this.hasCallOnlineAgents) return false;

    const timeWaiting = Number(this.waitTimes.call?.slice(1));
    return timeWaiting >= this.longTimeWaiting.MIN_WAIT_TIME;
  }

  get hasAlternativeOption () {
    return (this.showLiveChat && !this.isLiveChatLongTimeWaiting) || (this.showCall && !this.isCallLongTimeWaiting) || this.showAddComment || this.showOpenRequest;
  }

  get isSelectedLongTimeWaitingOption () {
    return this.longTimeWaiting.selectedOption !== null;
  }

  get selectedLongTimeWaitingOption () {
    const isChatAndCallHighVolume = this.isLiveChatLongTimeWaiting && this.isCallLongTimeWaiting;

    if (isChatAndCallHighVolume) {
      return this.$t('page.connect.both_live_and_call');
    }

    if (this.isLiveChatLongTimeWaiting) {
      return this.$t('page.connect.live_chat');
    }

    if (this.isCallLongTimeWaiting) {
      return this.$t('page.connect.call');
    }
  }

  get isFromActiveConversation () {
    return Boolean(this.activeConversationTicket?.id);
  }

  get trackLogContext() {
    return this.isFromActiveConversation 
      ? CMP_CONTEXTS.ACTIVE_CONVERSATION_TICKET 
      : CMP_CONTEXTS.CONNECT_REQUEST;
  }

  openThresholdModal () {
    let dialog: {
      close(): void;
      loading(val: boolean): void;
    };

    const closeDialog = () => {
      dialog.close();
    };

    const onLiveChat = () => {
      this.select(1);
      closeDialog();
    };

    const onCall = () => {
      this.select(2);
      closeDialog();
    };

    const onOpenRequest = () => {
      this.select(3);
      closeDialog();
    };

    dialog = this.$dialog({
      componentBackground: "transparent",
      content: (
        <ThresholdLimitModal
          liveChatWaitTimeDescription={this.liveChatWaitTimeDescription}
          callWaitTimeDescription={this.callWaitTimeDescription}
          hasLiveChatOnlineAgents={this.hasLiveChatOnlineAgents}
          hasCallOnlineAgents={this.hasCallOnlineAgents}
          onLiveChat={onLiveChat}
          onCall={onCall}
          onOpenRequest={onOpenRequest}
          onBack={closeDialog}
        />
      )
    });
  }

  getChatCallText (form: 'singular' | 'plural') {
    const choice = form === 'singular' ? 1 : 2;
    const isChatAndCallHighVolume = this.isLiveChatLongTimeWaiting && this.isCallLongTimeWaiting;

    if (isChatAndCallHighVolume) {
      return this.$tc('page.connect.long_wait_time.chats_calls', choice);
    }

    if (this.showLiveChat && this.isLiveChatLongTimeWaiting) {
      return this.$tc('page.connect.long_wait_time.chat', choice);
    }

    if (this.showCall && this.isCallLongTimeWaiting) {
      return this.$tc('page.connect.long_wait_time.call', choice);
    }
  }

  get stillPreferText () {
    return this.getChatCallText('singular');
  }

  get highVolumeText () {
    return this.getChatCallText('plural');
  }

  get suggestionText () {
    let text = '';

    const suggestLiveChat = this.showLiveChat && !this.isLiveChatLongTimeWaiting;
    const suggestCall = this.showCall && !this.isCallLongTimeWaiting;

    if (suggestLiveChat) {
      text += `${this.$t('page.connect.long_wait_time.start_a')} <strong>${this.$t('page.connect.live_chat')}</strong> `;
    } else if (suggestCall) {
      text += `<strong>${this.$t('page.connect.long_wait_time.call_us')}</strong> `;
    }

    if (this.showOpenRequest) {
      const showOr = suggestLiveChat || suggestCall;
      text += `${showOr ? this.$t('page.connect.long_wait_time.or') : ''} <strong>${this.$t('page.connect.long_wait_time.open_a_request')}</strong>`;
    }

    if (this.showAddComment) {
      const showOr = suggestLiveChat || suggestCall || this.showOpenRequest;
      text += `${showOr ? this.$t('page.connect.long_wait_time.or') : ''} <strong>${this.$t('page.connect.follow_up.text')}</strong>`;
    }

    return text;
  }

  get showLiveChat () {
    return !this.forceHide.chat && this.showLoggedInOptions.showLiveChat;
  }

  get hasLiveChatOnlineAgents () {
    return this.onlineAgents.chat;
  }

  get liveChatWaitTimeDescription () {
    return `${this.$tc('page.connect.customers_waiting', this.queuePosition, { customersNum: this.queuePosition })} ${this.$t('page.connect.wait_time', { mins: this.isLiveChatLongTimeWaiting ? `> ${this.longTimeWaiting.MIN_WAIT_TIME}` : this.waitTimes.chat })}`;
  }

  get showCall () {
    return !this.forceHide.call && this.showLoggedInOptions.showCall;
  }

  get hasCallOnlineAgents () {
    return this.onlineAgents.call;
  }

  get callWaitTimeDescription () {
    return `${this.$t('page.connect.wait_time', { mins: this.isCallLongTimeWaiting ? `> ${this.longTimeWaiting.MIN_WAIT_TIME}` : this.waitTimes.call })}`;
  }

  get showOpenRequest () {
    if (
      !this.showAddComment &&
      (!this.showCall || !this.hasCallOnlineAgents) &&
      (!this.showLiveChat || !this.hasLiveChatOnlineAgents)
    ) return true;

    return this.showLoggedInOptions.showOpenRequest;
  }

  get overloadedChannel () {
    const isChatAndCallHighVolume =
      this.isLiveChatLongTimeWaiting && this.isCallLongTimeWaiting;

    return isChatAndCallHighVolume
      ? OverloadedChannel.CHAT_AND_VOICE_CHAT
      : this.isLiveChatLongTimeWaiting
        ? OverloadedChannel.CHAT
        : this.isCallLongTimeWaiting
          ? OverloadedChannel.VOICE_CHAT
          : null;
  }

  async checkOnlineAgents() {
    const ticketId = this.activeConversationTicket?.id;
    const [onlineAgents, connectSettings] = await Promise.all([PleasedApi.getOnlineAgentsCheck({
      chat: this.showLiveChat,
      voice: this.showCall,
    }), ticketId ?
          PleasedApi.getActiveConversationConnectSettings({ticketId}).catch(() => ({ onlineChatAgentsCheck: true, onlineCallAgentsCheck: true })) :
          Promise.resolve({ onlineChatAgentsCheck: true, onlineCallAgentsCheck: true } as PleasedModel.OnlineAgentsCheck)]);

    if (ticketId) {
      if (!connectSettings.onlineChatAgentsCheck) {
        this.forceHide.chat = true;
        onlineAgents.onlineChatAgentsCheck = false;
      }

      if (!connectSettings.onlineCallAgentsCheck) {
        this.forceHide.call = true;
        onlineAgents.onlineCallAgentsCheck = false;
      }
    }

    this.setOnlineAgents(onlineAgents);

    analytics.track({
      name: `${this.trackLogContext}${COMPONENT_EVENTS.CONNECT_OPTIONS_SHOWN}`,
      ...(this.isFromActiveConversation ? { selection: this.activeConversationTicket.id } : {}),
      metadata: {
        chatShown: this.showLiveChat,
        callShown: this.showCall,
        ...(!this.isFromActiveConversation ? { openRequestShown: this.showOpenRequest } : {}),
      },
    })
  }

  async refreshChannels () {
    // In case of double/several clicks throttle requests via waiting for 2 seconds
    if (this.refreshChannelsLoading) {
      this.refreshChannelsThrottle = true;
      await waitFor(2000);
      this.refreshChannelsThrottle = false;
      this.refreshChannelsLoading = false;
      return;
    }

    this.refreshChannelsLoading = true;
    await this.checkOnlineAgents();

    // If there is no throttle, set loading to false
    if (!this.refreshChannelsThrottle) {
      this.refreshChannelsLoading = false;
    }
  }

  async created () {
    document.title = 'Contact Options';
    this.savedRequestInfo = { ...this.requestInfo };

    this.loading = true;
    try {
      const [queue, _, waitTimes] = await Promise.all([
        PleasedApi.getQueuePosition().catch(() => ({ position: 1 })),
        this.checkOnlineAgents(),
        PleasedApi.getWaitTimes().catch(noop),
      ]);

      this.queuePosition = queue.position;
      this.setWaitTimes(waitTimes);
    } catch {}
    this.loading = false;
  }

  setOnlineAgents (onlineAgents: PleasedModel.OnlineAgentsCheck) {
    this.onlineAgents = {
      chat: onlineAgents.onlineChatAgentsCheck ?? null,
      call: onlineAgents.onlineCallAgentsCheck ?? null,
    };
  }

  setWaitTimes (waitTimes: PleasedModel.WaitingTime | null | void) {
    if (!waitTimes) return;
    const times = {
      chat: waitTimes?.averageChatWaitingTime || null,
      call: waitTimes?.averageCallWaitingTime || null,
    };

    const waitTimeOrders = Object.entries(times).sort(([, time1], [, time2]) => {
      if (time1 === time2) return 0;
      if (time1 == null) return 1;
      if (time2 == null) return -1;
      const [numericTime1, numericTime2] = [time1.replace(/\D/g, '') || 0, time2.replace(/\D/g, '') || 0].map(n => Number(n));
      return numericTime1 > numericTime2 ? 1 : -1;
    }).map(([type, value]) => ([type, Number(value?.slice(1))]));

    const order = Object.fromEntries(waitTimeOrders);

    this.waitTimes = {
      ...times,
      order,
    };
  }

  handleWrapperSelect({ id }: { id: string }) {
    if (id === 'refresh') {
      analytics.trackClick({
        name: `${this.trackLogContext}${COMPONENT_EVENTS.CONNECT_OPTIONS_REFRESH_CHANNEL}`,
        ...(this.isFromActiveConversation ? { selection: this.activeConversationTicket.id } : {}),
      });
      this.refreshChannels();
    } else if (id === 'open-request') {
      this.handleOpenRequest();
    }
  }

  handleOpenRequest () {
    reportingService.openRequestClicked();
    this.$gtag.event(GA_EVENT_NAME_ENUM.START_OPEN_REQUEST_CREATION);
    this.sendSelectedOptionLog(SelectedChannel.OPEN_REQUEST);
    this.loading = true;

    const hadAiConversation = this.connectConfig?.aiConversation;

    const tags = [
      this.connectConfig?.appliedMetadataAutomation?.tag,
    ].filter(notEmpty);
    
    PleasedApi.createTicket({
      subject: this.requestInfo.subject,
      description: this.requestInfo.description,
      recipientAddress: this.requestInfo.email,
      ticketFields: this.ticketFields,
      userSelections: this.userSelections,
      userLogs: reportingService.getLogs(),
      tags,
      chatbotSessionId: analytics.sessionId,
      ...(hadAiConversation ? { aiSessionId: analytics.sessionId } : {}),
    })
      .then(res => {
        const $event: ConnectRequestEvent = {
          type: 'openRequest',
          payload: {
            ticketId: res.id,
          },
        };

        analytics.track({
          name: `${this.trackLogContext}${COMPONENT_EVENTS.CONNECT_OPTIONS_OPEN_REQUEST_CREATED}`,
          selection: res.id,
        });

        this.$gtag.event(GA_EVENT_NAME_ENUM.OPEN_REQUEST_CREATED);
        this.$emit('connect:request', $event);
      })
      .catch(e => {
        const error: Error = e;
        this.$gtag.event(GA_EVENT_NAME_ENUM.OPEN_REQUEST_CREATION_FAILED);
        this.errInfo.isErr = true;
        this.errInfo.message = error.message;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  async sendSelectedOptionLog (selectedChannel: SelectedChannelType) {
    // Track only when there is an overloaded channel
    if (!this.overloadedChannel) return;
    await PleasedApi.sendDeflectionLog({ selectedChannel, overloadedChannel: this.overloadedChannel });
  }

  select (id: number) {
    if (id === 1) {
      if (!this.hasLiveChatOnlineAgents || this.isVoiceChatLoading || this.isLiveChatLoading) return;

      if (this.isLiveChatLongTimeWaiting && !this.isSelectedLongTimeWaitingOption) {
        this.longTimeWaiting.selectedOption = 'liveChat';
        return;
      }

      const $event: ConnectRequestEvent = {
        type: 'chat',
        payload: {
          description: this.requestInfo.description,
          isFromActiveConversation: this.isFromActiveConversation,
        },
      };
      this.sendSelectedOptionLog(SelectedChannel.CHAT);
      this.$emit('connect:request', $event);
    } else if (id === 2) {
      if (!this.hasCallOnlineAgents || this.isVoiceChatLoading || this.isLiveChatLoading) return;

      if (this.isCallLongTimeWaiting && !this.isSelectedLongTimeWaitingOption) {
        this.longTimeWaiting.selectedOption = 'call';
        return;
      }

      const $event: ConnectRequestEvent = {
        type: 'voice',
        payload: {
          isFromActiveConversation: this.isFromActiveConversation,
          description: this.requestInfo.description,
          query: this.requestInfo.subject || '',
          deflectionLog: this.overloadedChannel ? { selectedChannel: SelectedChannel.VOICE_CHAT, overloadedChannel: this.overloadedChannel } : undefined,
        },
      };

      this.$emit('connect:request', $event);
    } else if (id === 3) {
      analytics.trackClick({
        name: `${this.trackLogContext}${COMPONENT_EVENTS.CONNECT_OPTIONS_OPEN_REQUEST}`,
        ...(this.isFromActiveConversation ? { selection: this.activeConversationTicket.id } : {}),
      });

      this.isOpeningRequest = true;
    } else if (id === 4) {
      const $event: ConnectRequestEvent = {
        type: 'addComment',
        payload: {},
      };
      this.sendSelectedOptionLog(SelectedChannel.OPEN_REQUEST);
      this.$emit('connect:request', $event);
    }
  }
  handleBack () {
    if (this.isOpeningRequest) {
      this.isOpeningRequest = false;
      this.$emit('update:request-info', this.savedRequestInfo);
      return;
    }

    if (this.isSelectedLongTimeWaitingOption) {
      this.longTimeWaiting.selectedOption = null;
      return;
    }

    this.$emit('back');
  }
  cleanErr () {
    this.errInfo.isErr = false;
    this.errInfo.message = '';
  }

  @Watch('showThresholdLimitModal')
  async onShowThresholdLimitModalChange () {
    if (this.showThresholdLimitModal) {
      await this.checkOnlineAgents();
      this.longTimeWaiting.selectedOption = null;
      this.openThresholdModal();
      this.$emit('set-show-threshold-limit-modal', false);
    }
  }

  @Watch('activeConversationTicket')
  async resetForceHide () {
    if (!this.isFromActiveConversation) {
      this.forceHide  = {
        chat: false,
        call: false
      }
    }
  }
}
